import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { X, Plus, Minus, ShoppingBag, Trash2, ArrowRight, Truck } from 'lucide-react';
import { categories, FREE_SHIPPING_THRESHOLD } from '../../data/mock';

const CartDrawer = () => {
  const { isCartOpen, closeCart, cartItems, cartTotal, updateQuantity, removeFromCart } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-ink-950/70 backdrop-blur-sm z-50" onClick={closeCart} />

      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 bg-ink-950 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-athletic-500 flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-xl tracking-wider">YOUR CART</h2>
              <p className="text-xs text-white/60">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button onClick={closeCart} className="p-2 hover:bg-white/10 transition-colors text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5 bg-ink-50">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-white border-2 border-ink-100 flex items-center justify-center mb-5">
                <ShoppingBag className="w-9 h-9 text-ink-300" />
              </div>
              <h3 className="font-display text-2xl text-ink-900 tracking-wider mb-2">CART IS EMPTY</h3>
              <p className="text-ink-500 text-sm mb-6">Gear up to start training.</p>
              <button onClick={closeCart} className="px-6 py-3 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider text-sm transition-colors">
                Browse Products
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map(item => {
                const cat = categories.find(c => c.id === item.categoryId);
                return (
                  <div key={item.id} className="flex gap-4 p-4 bg-white border border-ink-100">
                    <div className="w-20 h-20 bg-ink-50 flex-shrink-0 overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-athletic-500 uppercase tracking-[0.25em] font-bold mb-0.5">{cat?.name || 'Sports Gear'}</p>
                      <h4 className="font-bold text-ink-900 text-sm leading-tight mb-1 line-clamp-2">{item.name}</h4>
                      <p className="text-ink-900 font-black">${item.price.toFixed(2)}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-ink-200">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-1.5 hover:bg-ink-50 text-ink-700">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-xs font-black text-ink-900">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-1.5 hover:bg-ink-50 text-ink-700">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button onClick={() => removeFromCart(item.id)} className="p-1.5 text-ink-400 hover:text-athletic-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-5 border-t-2 border-ink-100 bg-white space-y-3">
            {cartTotal < FREE_SHIPPING_THRESHOLD ? (
              <div className="bg-ink-50 border border-ink-100 p-3">
                <div className="flex items-center gap-2 text-xs mb-2">
                  <Truck className="w-4 h-4 text-athletic-500" />
                  <span className="text-ink-700 font-bold">
                    ${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)} MORE FOR FREE SHIPPING
                  </span>
                </div>
                <div className="w-full bg-ink-100 h-1.5 overflow-hidden">
                  <div className="h-full bg-athletic-500 transition-all" style={{ width: `${Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }} />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-3 bg-athletic-500 text-white">
                <Truck className="w-5 h-5" />
                <span className="text-sm font-black uppercase tracking-wider">Free Shipping Unlocked!</span>
              </div>
            )}

            <div className="flex items-baseline justify-between pt-2">
              <span className="text-ink-600 font-bold uppercase text-xs tracking-wider">Subtotal</span>
              <span className="font-display text-3xl text-ink-900 leading-none">${cartTotal.toFixed(2)}</span>
            </div>

            <Link to="/checkout" onClick={closeCart} className="flex items-center justify-center gap-2 w-full py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider transition-all">
              Checkout <ArrowRight className="w-5 h-5" />
            </Link>

            <button onClick={closeCart} className="w-full text-center text-ink-500 hover:text-ink-900 py-2 text-xs font-bold uppercase tracking-wider transition-colors">
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
